import { DatePicker as AntdDatePicker, DatePickerProps as AntDatePickerProps } from 'antd';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import IMask from 'imask';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { formatDateStringToISO, getDateWithoutTime } from '@/helpers/dates';

import AuthContext from '@/contexts/Auth/AuthContext';

import { FormInput } from '../FormInput/FormInput';
import { Icon } from '../Icon/Icon';

import './DatePicker.scss';

const todayDay = dayjs(getDateWithoutTime(new Date()));
const hundredYearsAgo = dayjs(getDateWithoutTime(new Date())).subtract(100, 'years');
const hundredYearsLater = dayjs(getDateWithoutTime(new Date())).add(100, 'years');

export interface DatePickerProps extends Omit<AntDatePickerProps, 'onChange'> {
  label?: string;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  maxDateToday?: boolean;
  minDateToday?: boolean;
  minDateBirth?: boolean;
  errorMessage?: string;
  format?: string;
  usedInModal?: boolean;
  onChange?: (date: Nullable<Dayjs>, formattedDateString: Nullable<string>) => void;
}

export const DatePicker = React.forwardRef<any, DatePickerProps>(
  (
    {
      label,
      minDate,
      maxDate,
      placeholder = 'дд.мм.рррр',
      format = 'DD.MM.YYYY',
      minDateToday = false,
      maxDateToday = false,
      minDateBirth = false,
      errorMessage,
      value,
      name,
      required,
      usedInModal = false,
      onChange,
      ...props
    },
    ref,
  ) => {
    const datePickerRef = useRef<any>(null);
    const ignoreNextOpenRef = useRef<boolean>(false);
    const { userData } = useContext(AuthContext);
    const [forceAntOpen, setForceAntOpen] = useState<boolean>(false);

    const calculatedMinDate = useMemo<Dayjs>(() => {
      if (minDate) {
        return dayjs(minDate);
      } else if (minDateBirth && userData?.date_of_birth) {
        return dayjs(userData.date_of_birth);
      } else if (minDateToday) {
        return todayDay;
      } else {
        return hundredYearsAgo;
      }
    }, [minDate, minDateBirth, minDateToday, userData?.date_of_birth]);

    const calculatedMaxDate = useMemo<Dayjs>(() => {
      if (maxDate) {
        return dayjs(maxDate);
      } else if (maxDateToday) {
        return todayDay;
      } else {
        return hundredYearsLater;
      }
    }, [maxDate, maxDateToday]);

    useEffect(() => {
      const inputElement = datePickerRef.current?.nativeElement?.querySelector('input');
      if (inputElement) {
        const mask = IMask(inputElement, {
          mask: format,
          lazy: false,
          blocks: {
            DD: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 31,
              maxLength: 2,
            },
            MM: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 12,
              maxLength: 2,
            },
            YYYY: {
              mask: IMask.MaskedRange,
              from: 1900,
              to: 2100,
              maxLength: 4,
            },
          },
        });

        const handleInput = () => {
          if (mask.unmaskedValue === '') {
            onChange?.(null, null);
          } else if (mask.masked.isComplete) {
            const parsedDate = dayjs(mask.value, format, true);

            const isDateInRange = (date: Dayjs): boolean => {
              const isAfterMin = calculatedMinDate ? date.isAfter(calculatedMinDate.subtract(1, 'day')) : true;
              const isBeforeMax = calculatedMaxDate ? date.isBefore(calculatedMaxDate.add(1, 'day')) : true;
              return isAfterMin && isBeforeMax;
            };

            if (parsedDate.isValid() && isDateInRange(parsedDate)) {
              onChange?.(parsedDate, formatDateStringToISO(mask.value));
            }
          }
        };

        inputElement.addEventListener('input', handleInput);

        return () => {
          mask.destroy();
          inputElement.removeEventListener('input', handleInput);
        };
      }
    }, [datePickerRef]);

    const handleOpenChange = (open: boolean): void => {
      if (open && ignoreNextOpenRef.current) {
        ignoreNextOpenRef.current = false;
        return;
      }
      setForceAntOpen(open);
    };

    const handleChange = (date: Nullable<Dayjs>, formattedDateString: string | string[]): void => {
      ignoreNextOpenRef.current = true;
      setForceAntOpen(false);
      if (!date) {
        onChange?.(date, undefined);
      } else {
        !Array.isArray(formattedDateString) && onChange?.(date, formatDateStringToISO(formattedDateString));
      }
    };

    return (
      <FormInput label={label} required={required} errorMessage={errorMessage}>
        <AntdDatePicker
          data-testid="date-picker"
          {...props}
          open={forceAntOpen}
          onOpenChange={handleOpenChange}
          ref={(node) => {
            datePickerRef.current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          format={format}
          name={name}
          value={value ? dayjs(value) : null}
          placeholder={placeholder}
          className={classNames('custom-input-date', classNames)}
          styles={{
            popup: {
              root: { width: '120px' },
            },
          }}
          minDate={calculatedMinDate}
          maxDate={calculatedMaxDate}
          status={errorMessage && 'error'}
          allowClear={{
            clearIcon: (
              <div className="custom-input-date__icons">
                <Icon name="close" color="grey" />
                <Icon name="calendar" color="grey" />
              </div>
            ),
          }}
          suffixIcon={
            !value ? (
              <Icon name="calendar" color="grey" />
            ) : (
              <div className="custom-input-date__icons">
                <Icon name="close" color="grey" />
                <Icon name="calendar" color="grey" />
              </div>
            )
          }
          onChange={handleChange}
          placement="bottomRight"
          // Popup must render inside modal to inherit z-index
          getPopupContainer={(triggerNode): HTMLElement =>
            usedInModal ? (triggerNode.parentNode as HTMLElement) : document.body
          }
        />
      </FormInput>
    );
  },
);
